import axios from 'axios';
import LocalStorageService from './LocalStorageService';
import TEXT from '../controller/TextVariables';
import { localDevelopment } from './StaticVariables';
import { getZoomFromTileKey } from './DataTransformation';
class DataServiceError extends Error {
    code;
    name;
    description;
    modalMessage;
    config;

    constructor(code = 101, message, options = {}) {
        //leave here for later troubleshooting
        if (!(code in TEXT.errorMessages)) {
            console.log(code);
            console.log(TEXT.errorMessages);
            console.log(message);
            console.log(options);
        }
        const { name, devNotes: description, message: modalMessage } = code in TEXT.errorMessages ? TEXT.errorMessages[code] : TEXT.errorMessages[101];
        const combinedMessage = `${code}: API MESSAGE: ${message}: ${description}`;
        super(combinedMessage, options);
        this.code = code;
        this.name = name;
        this.description = description;
        this.modalMessage = modalMessage;
        this.config = options.config;
    }
}

class DataService {
    _verbose = false;
    _localStorageService = LocalStorageService;
    _needsAuth = false;
    _name = 'Data Service';
    _client = axios.create({
        baseURL: '',
        params: { token: '' }
    });
    _requestCount = 0;
    _authClient = axios.create({});
    _queue = [];
    _authStatus = 0; //0. Unset, 1. Authenticated, -1. Invalid username and Password
    _token = false;
    _axiosSource = axios.CancelToken.source();

    constructor(props) {
        this._needsAuth = props?._needsAuth;
        this._name = props?._name;

        this._token = this.authenticate()
            .then((res) => {
                this._authStatus = 1;
                return res;
            })
            .catch((err) => {
                this._authStatus = -1;
                return false;
            });

        this._client.interceptors.request.use(this.requestInterceptor);
        this._client.interceptors.response.use(this.responseInterceptor, this.responseError);
        //ΔVΔVΔ: Uncomment to rest token refresh
        // setTimeout(async() => {
        //     console.log('BRK: ')
        //   this._token = await this._token +'break'
        // },10 * 1000)
    }
    responseError = async (err) => {
        if (err.code === 498 && !err.config._retry) {
            this._token = await this.authenticate();
            return this._client({ _retry: true, ...err.config });
        }
        return Promise.reject(err);
    };
    responseInterceptor = (res) => {
        //ΔVΔVΔ: Error Handling
        if (res.data?.[0]?.status === 'error' || res.data?.error) {
            const rawError = res.data?.[0] || res.data.error;
            var code = res.data?.error?.code || 505;
            var codeDetails = rawError.details?.[0]?.toLowerCase();
            if (codeDetails && codeDetails?.indexOf('too many') !== -1) {
                code = 405;
            }
            if (codeDetails && codeDetails.indexOf('invalid username or password') !== -1) {
                code = 401;
            }
            const message = res.data.message || res.data?.error?.message;
            var error = new DataServiceError(code, message, { ...res });
            throw error;
        }
        return res;
    };
    _callsAreAuthorized = async () => {
        if (!this._needsAuth) return true;
        if (this._authStatus === -1) return false;
        if ((await this._token) === 'invalid') return false;
        return true;
    };
    requestInterceptor = async (config) => {
        //if congif has keys: data,reponse, or headers, then this interceptor was set to response on accident
        if (!this._needsAuth || config.params?.needsAuth === 'false') {
            if (config.params.hasOwnProperty('token')) delete config.params.token;
            if (config.params.needsAuth) delete config.params.needsAuth;
            return config;
        }
        // ΔVΔVΔ:remove below validation with _callsAreAuthorized
        const tok = await this._token; //await for inherited calls
        if (tok === 'invalid') {
            throw this._client.cancel('Authorization faild ignore request');
        }
        if (config?.body) config.body.token = tok;
        if (config?.params) config.params.token = tok;
        return config;
    };
    // ΔVΔVΔ: Function that takes args and uses a switch case to return the request config to pass to axios for a lookup
    _api = (args) => {
        return { url: '', body: {}, head: {} };
    };
    // ΔVΔVΔ: Adds additional pre-flight formatting for config calls
    formatCall = (args) => {
        const callObj = this._api(args);
        callObj.args = args;
        this.applyFilterAuth(callObj);
        return callObj;
    };

    // ΔVΔVΔ: Use for inheritance
    applyFiltertransformData = (callObj, res) => {
        if (!res || !res.data) return [];
        return res.data ? res.data : [];
    };
    //ΔVΔVΔ: Funtion that returns the request config to pass to axios for the authorization lookup
    getAuthCall = () => {
        return { url: '', body: {}, head: {} };
    };
    // ΔVΔVΔ: Format api Operation slug
    formatSlug = (args) => {
        const { type, scope, tract } = args;
        return [type, scope, tract]
            .filter((a) => a)
            .join('_')
            .toLocaleLowerCase()
            .replace(/ /g, '_');
    };
    // ΔVΔVΔ: Check if authentication token is needed/exists otherwise run the auth call and return the token
    authenticate = async () => {
        if (!this._needsAuth) return 'N/A';

        const startToken = this._localStorageService.getAccessToken();
        if (startToken && startToken === 'invalid') {
            // throw new Error("invalid token clear cache");
            return 'invalid';
        }
        if (startToken && startToken !== 'undefined') return startToken;
        const callObj = this.getAuthCall();
        return await axios
            .post(callObj.url, callObj.body)
            .then((res) => {
                this.responseInterceptor(res); //see if there is an error and throw
                let dat = this.applyFiltertransformData(callObj, res);
                this._localStorageService.setToken({ access_token: dat.token, expires: dat.expires });
                return dat.token;
            })
            .catch((err) => {
                this._localStorageService.setToken({ access_token: 'invalid' });
                throw err;
            });
    };
    // ΔVΔVΔ: Alters the config to add authorization based on api requirments
    applyFilterAuth = (callObj) => {
        if (this._needsAuth && callObj.url.length) {
            callObj.body.token = this._localStorageService.getAccessToken();
        }
        return callObj;
    };

    get = async (callObj, allowParamTransform = true) => {
        this._requestCount++;
        const needsAuth = this._needsAuth;
        const id = this._requestCount;
        return this._client
            .get(callObj.url, {
                params: callObj.body,
                cancelToken: this._axiosSource.token,
                paramsSerializer: function paramsSerializer(params) {
                    if (!needsAuth) delete params.token; //ΔVΔVΔ: remove token from config if this data service does not need authentication
                    return Object.entries(Object.assign({}, params))
                        .map(([key, value]) => `${key}=${value}`)
                        .join('&');
                }
            })
            .then((res) => {
                return this.applyFiltertransformData(callObj, res);
            });
        // .catch((err) => {
        //     console.log("DATA SERvice", err);
        //     // return this.onError(callObj, err);
        //     throw err;
        // });
    };
    post = async (callObj) => {
        const needsAuth = this._needsAuth;
        if (!needsAuth) delete callObj.body.token;
        return this._client.post(callObj.url, callObj.body).then((res) => {
            return this.applyFiltertransformData(callObj, res);
        });
    };
    getListData = async (scope) => {
        const callObj = this.formatCall({ type: 'list', scope });
        return this.get(callObj).then((data) => {
            return data.Table ? data.Table : data; //this may be specofoc to an a, maybe move to the api specific file as a data transform
        });
    };
    getPOIData = async (props) => {
        const { scope, extent, year, tileKey } = props;
        let zoom = getZoomFromTileKey(tileKey);
        if (localDevelopment) {
            const url = `/mock/${tileKey.replace(/:/g, '_')}_${zoom}_nursinghome_poi.json`;
            // const url = `/mock/${tileKey.replace(/:/g, '_')}_${zoom}_${scope}_poi.json`;
            return fetch(url)
                .then((data) => data.json())
                .catch(() => {
                    return { Table: [], Table1: [{ Count: 0 }] };
                });
        }
        // console.log(props);
        const callObj = this.formatCall({ type: 'poi', scope, extent, year, tileKey });
        return this.post(callObj).then((data) => {
            // var filename = `${tileKey}_${zoom}_${scope}_poi.json`;
            // if (!downloadList[filename]) {
            //     var jsonstr = jsonToDownloadString(data);
            //     downloadString(jsonstr, filename);
            //     downloadList[filename] = true;
            //     console.log(downloadList);
            // }
            return data;
            // return data.Table ? data.Table : data;
            // return { Table: [], Table1: [{ Count: 0 }] };
        });
    };
    getGeometryData = async (props) => {
        const { type = 'geometry', scope, extent, tract, state, year } = props;
        const callObj = this.formatCall({ type, scope, tract, state, year, extent });
        return await this.get(callObj);
    };
    getSVIData = async (props) => {
        const { type = 'svi', scope, extent, tract, state, year } = props;
        const callObj = this.formatCall({ type, scope, tract, state, year, extent });
        return await this.post(callObj);
    };
    getBaseMapPath = () => {
        const { type = 'base', scope = 'geometry' } = {};
        return this.formatCall({ type, scope }).url;
    };
    getBaseStyleData = async (props) => {
        //base_style
        const { type = 'base', scope = 'style' } = {};
        const callObj = this.formatCall({ type, scope });
        return this.get(callObj);
    };
}
export { DataService };

// https://onemap.cdc.gov/OneMapServices/rest/services/SVI/SVI_consolidated_data/FeatureServer/0/query?where=year%3D2018

// // JSON call
// //https://onemap.cdc.gov/OneMapServices/rest/services/SVI/SVI_consolidated_data/FeatureServer/0/query?where=year%3D2018&f=pjson

// //Returns Json all Fields for year 2018
// // https://onemap.cdc.gov/OneMapServices/rest/services/SVI/SVI_consolidated_data/FeatureServer/0/query?where=year%3D2018&outFields=*&returnGeometry=false&f=pjson

// // Returns pbf
// // https://onemap.cdc.gov/OneMapServices/rest/services/SVI/SVI_consolidated_data/FeatureServer/0/query?where=year%3D2018&outFields=*&returnGeometry=false&f=pbf

// https://onemap.cdc.gov/OneMapServices/rest/services/SVI/SVI_consolidated_data/FeatureServer/0/query?where=year%3D2018+AND+comparison%3D%27national%27+AND+ST%3D%2701%27&objectIds=&time=&geometry=&geometryType=esriGeometryEnvelope&inSR=&spatialRel=esriSpatialRelIntersects&distance=&units=esriSRUnit_Foot&relationParam=&outFields=&returnGeometry=true&maxAllowableOffset=&geometryPrecision=&outSR=&havingClause=&gdbVersion=&historicMoment=&returnDistinctValues=false&returnIdsOnly=false&returnCountOnly=false&returnExtentOnly=false&orderByFields=&groupByFieldsForStatistics=&outStatistics=&returnZ=false&returnM=false&multipatchOption=xyFootprint&resultOffset=&resultRecordCount=&returnTrueCurves=false&returnExceededLimitFeatures=false&quantizationParameters=&returnCentroid=false&sqlFormat=none&resultType=&featureEncoding=esriDefault&datumTransformation=&f=json
// https://onemap.cdc.gov/OneMapServices/rest/services/SVI/SVI_consolidated_data/FeatureServer/0/query?where=year%3D2018+AND+comparison%3D%27national%27+AND+ST%3D%2701%27&objectIds=&time=&geometry=&geometryType=esriGeometryEnvelope&inSR=&spatialRel=esriSpatialRelIntersects&distance=&units=esriSRUnit_Foot&relationParam=&outFields=&returnGeometry=true&maxAllowableOffset=&geometryPrecision=&outSR=&havingClause=&gdbVersion=&historicMoment=&returnDistinctValues=false&returnIdsOnly=false&returnCountOnly=false&f=json
// https://onemap.cdc.gov/OneMapServices/rest/services/SVI/SVI_consolidated_data/FeatureServer/0/query?where=year%3D2018+AND+comparison%3D%27national%27+AND+ST%3D%2701%27&f=json
// https://onemap.cdc.gov/OneMapServices/rest/services/SVI/SVI_consolidated_data/FeatureServer/0/query?where=year=2018+AND+Comparison=%3Dnational%3D+AND+geoLevel=%3Dcensus%3D&f=JSON
// https://onemap.cdc.gov/OneMapServices/rest/services/SVI/SVI_consolidated_data/FeatureServer/0/query?where=year%3D2018+AND+comparison%3D%27national%27+AND+ST%3D%2701%27&f=json
// https://onemap.cdc.gov/OneMapServices/rest/services/SVI/SVI_consolidated_data/FeatureServer/0/query?where=year%3D2018+AND+Comparison%3D%27national%27+AND+geoLevel%3D%27census%27&f=JSON
// https://onemap.cdc.gov/OneMapServices/rest/services/SVI/SVI_consolidated_data/FeatureServer/0/query?where=year%3D2018+AND+comparison%3D%27national%27+AND+ST%3D%2701%27&f=json REFRERENCE
